import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiEdit, FiTrash2, FiPlusCircle, FiXCircle } from 'react-icons/fi';

const PartyManager = () => {
  const [parties, setParties] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    type: 'customer',
    phone: '',
    address: '',
  });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchParties();
  }, []);

  const fetchParties = async () => {
    try {
      const { data } = await axios.get('https://api.pranucollection.com/api/parties');
      setParties(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching parties:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`https://api.pranucollection.com/api/parties/${editId}`, formData);
      } else {
        await axios.post('https://api.pranucollection.com/api/parties/add', formData);
      }
      resetForm();
      await fetchParties();
    } catch (error) {
      console.error('Error saving party:', error);
    }
  };

  const handleEdit = (party) => {
    setFormData({
      name: party.name,
      type: party.type,
      phone: party.phone,
      address: party.address,
    });
    setEditId(party._id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this party?')) {
      try {
        await axios.delete(`https://api.pranucollection.com/api/parties/${id}`);
        await fetchParties();
      } catch (error) {
        console.error('Error deleting party:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', type: 'customer', phone: '', address: '' });
    setEditId(null);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Form Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {editId ? 'Edit Business' : 'Add New Business'}
          </h2>
          {editId && (
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <FiXCircle size={24} />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Form fields same as before */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
              <input
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="customer">Customer</option>
                <option value="supplier">Supplier</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                type="tel"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <input
                name="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors flex items-center"
            >
              {editId ? (
                <>
                  <FiEdit className="mr-2" />
                  Update Business
                </>
              ) : (
                <>
                  <FiPlusCircle className="mr-2" />
                  Add Business
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Parties List Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">Business Directory</h3>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading businesses...</div>
        ) : parties.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No businesses found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Contact</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Address</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {parties.map((party) => (
                  <tr key={party._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{party.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">{party.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{party.phone || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{party.address || '-'}</td>
                    <td className="px-6 py-4 text-sm text-right space-x-4">
                      <button
                        onClick={() => handleEdit(party)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <FiEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(party._id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartyManager;